import React, { useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useAssessment } from '../hooks/useAssessment';
import { calculateScores, getMaturityLevel } from '../utils/scoreCalculator';
import ResultChart from '../components/Assessment/ResultChart';
import Button from '../components/Shared/Button';
import { theme } from '../styles/theme';
import { sendAssessmentEmails } from '../utils/emailService';

const ResultsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const LevelBadge = styled.div`
  display: inline-block;
  background-color: ${theme.colors.primary};
  color: white;
  padding: 8px 24px;
  border-radius: 50px;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 20px 0;
  box-shadow: 0 4px 6px rgba(13, 127, 233, 0.3);
`;

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
`;

const DetailedScores = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DimensionScoreCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid ${theme.colors.secondary};
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const ScoreBar = styled.div<{ score: number }>`
  height: 6px;
  background-color: ${theme.colors.lightGray};
  border-radius: 3px;
  margin-top: 10px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => (props.score / 5) * 100}%;
    background-color: ${props => {
        if (props.score < 2) return theme.colors.danger;
        if (props.score < 4) return theme.colors.warning;
        return theme.colors.success;
    }};
    border-radius: 3px;
  }
`;

const ActionSection = styled.div`
    margin-top: 60px;
    text-align: center;
    padding: 40px;
    background-color: #FAFAFA;
    border-radius: 8px;
`;

const FollowUpSection = styled.div`
  margin-top: 60px;
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
`;

const FollowUpTitle = styled.h2`
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.medium};
`;

const FollowUpMessage = styled.p`
  color: ${theme.colors.secondary};
  font-size: 1.1rem;
  margin-bottom: ${theme.spacing.large};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.medium};
  justify-content: center;
  margin-top: ${theme.spacing.large};
`;

const NoButton = styled(Button)`
  background: #95a5a6;
  
  &:hover {
    background: #7f8c8d;
  }
`;

const SuccessMessage = styled.div`
  background: #E8F7E8;
  border: 2px solid #4CAF50;
  padding: 30px;
  border-radius: 8px;
  margin-top: 30px;
  
  h3 {
    color: #2E7D32;
    margin: 0 0 10px 0;
  }
  
  p {
    color: #555;
    margin: 5px 0;
  }
`;

const ErrorMessage = styled.div`
  background: #FFEBEE;
  border: 2px solid #F44336;
  padding: 30px;
  border-radius: 8px;
  margin-top: 30px;
  
  h3 {
    color: #C62828;
    margin: 0 0 10px 0;
  }
  
  p {
    color: #555;
    margin: 5px 0;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const AISparkle = styled.span`
  display: inline-block;
  margin-right: 8px;
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
  }
`;

const Results = () => {
  const history = useHistory();
  const { dimensions, answers, resetAssessment, userInfo } = useAssessment();
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [interestedInFollowUp, setInterestedInFollowUp] = useState(false);

  // If no answers, redirect home
  if (Object.keys(answers).length === 0) {
      history.replace('/');
      return null;
  }

  const { dimensionScores, overallScore } = calculateScores(dimensions, answers);
  const maturityLevel = getMaturityLevel(overallScore);

  const dimensionLabels = dimensions.reduce((acc, dim) => {
    acc[dim.id] = dim.name;
    return acc;
  }, {} as { [key: string]: string });

  const handleFollowUpResponse = async (interested: boolean) => {
    if (!userInfo) {
      alert('User information not found. Please restart the assessment.');
      return;
    }

    setSending(true);
    setSendError(false);
    setInterestedInFollowUp(interested);
    
    const success = await sendAssessmentEmails({
      userInfo,
      dimensionScores,
      overallScore,
      maturityLevel,
      interestedInFollowUp: interested,
    });

    setSending(false);
    
    if (success) {
      setEmailSent(true);
    } else {
      setSendError(true);
    }
  };

  const handleRetake = () => {
    resetAssessment();
    history.push('/');
  };

  return (
    <ResultsContainer>
      <Header>
        <h1>Assessment Complete</h1>
        <p style={{ color: theme.colors.secondary, fontSize: '1.2rem', marginTop: '10px' }}>
            Your organization's AI Maturity Profile
        </p>
        <LevelBadge>{maturityLevel}</LevelBadge>
        <p>Overall Score: <strong>{overallScore.toFixed(1)} / 5.0</strong></p>
      </Header>

      <ScoreGrid>
        <ChartSection>
          <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>Maturity Radar</h3>
          <ResultChart scores={dimensionScores} labels={dimensionLabels} />
        </ChartSection>

        <DetailedScores>
          <h3>Dimension Breakdown</h3>
          {dimensions.map(dim => (
            <DimensionScoreCard key={dim.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <strong>{dim.name}</strong>
                <span>{dimensionScores[dim.id].toFixed(1)}/5</span>
              </div>
              <ScoreBar score={dimensionScores[dim.id]} />
              <p style={{ fontSize: '0.85rem', color: theme.colors.secondary, marginTop: '8px' }}>
                  {dim.description}
              </p>
            </DimensionScoreCard>
          ))}
        </DetailedScores>
      </ScoreGrid>

      {!emailSent && !sendError ? (
        <FollowUpSection>
          <FollowUpTitle>Next Steps</FollowUpTitle>
          <FollowUpMessage>
            Based on your assessment results, we recommend prioritising the dimensions with the lowest scores to address key gaps in your AI readiness.
          </FollowUpMessage>
          <FollowUpMessage>
            A detailed assessment report highlighting your strengths, gaps, and recommended actions will be shared with you via email.
          </FollowUpMessage>
          <FollowUpMessage style={{ fontWeight: 600, color: theme.colors.text, marginTop: '30px' }}>
            Would you like our team to reach out to you to discuss the results and next steps?
          </FollowUpMessage>
          
          <ButtonGroup>
            <NoButton 
              onClick={() => handleFollowUpResponse(false)} 
              disabled={sending}
            >
              {sending ? (
                <>
                  <LoadingSpinner />
                  <AISparkle>✨</AISparkle>
                  Sending...
                </>
              ) : (
                'No'
              )}
            </NoButton>
            <Button 
              onClick={() => handleFollowUpResponse(true)} 
              disabled={sending}
            >
              {sending ? (
                <>
                  <LoadingSpinner />
                  <AISparkle>✨</AISparkle>
                  Sending...
                </>
              ) : (
                'Yes'
              )}
            </Button>
          </ButtonGroup>
        </FollowUpSection>
      ) : emailSent ? (
        <FollowUpSection>
          <SuccessMessage>
            <h3>✓ Thank You!</h3>
            <p>We've successfully sent your detailed assessment report to <strong>{userInfo?.email}</strong></p>
            {interestedInFollowUp ? (
              <p>Our team will review your results and reach out to you shortly to discuss next steps.</p>
            ) : (
              <p>You can review your detailed report at your convenience.</p>
            )}
          </SuccessMessage>
          
          <div style={{ marginTop: '30px' }}>
            <Button onClick={handleRetake}>Take Another Assessment</Button>
          </div>
        </FollowUpSection>
      ) : (
        <FollowUpSection>
          <ErrorMessage>
            <h3>⚠ Oops! Something went wrong</h3>
            <p>We couldn't send your assessment report. This might be a temporary network issue.</p>
            <p>Please try again, or contact us directly at <a href="mailto:sales@kovaion.com" style={{color: '#C62828', fontWeight: 600}}>sales@kovaion.com</a></p>
          </ErrorMessage>
          
          <ButtonGroup style={{ marginTop: '30px' }}>
            <NoButton onClick={() => setSendError(false)}>
              Go Back
            </NoButton>
            <Button onClick={() => handleFollowUpResponse(interestedInFollowUp)}>
              {sending ? (
                <>
                  <LoadingSpinner />
                  Retrying...
                </>
              ) : (
                'Try Again'
              )}
            </Button>
          </ButtonGroup>
        </FollowUpSection>
      )}
    </ResultsContainer>
  );
};

export default Results;